import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { SearchBox } from "../../../../components/Search";
import "../Mod/ModTable/ModIndex.css";

// ─── Modal (same pattern as ModFilter) ───────────────────
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  width = "600px",
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
      const t = setTimeout(() => setIsMounted(false), 160);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isMounted ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMounted]);

  useEffect(() => {
    if (!isMounted) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isMounted, onClose]);

  if (!isMounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-200 ease-in-out ${
          isAnimating ? "opacity-50" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        ref={modalRef}
        className={`relative bg-white shadow-xl flex flex-col overflow-hidden transition-all duration-200 ease-in-out ${
          isAnimating
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-2 scale-95"
        }`}
        style={{
          width,
          maxWidth: "100vw",
          maxHeight: "90vh",
          borderRadius: "12px",
          border: "3px solid #F3F5F8",
          zIndex: 10000,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

// ─── ClassesFilter ────────────────────────────────────────
// Bulk-edit quantum / rate / bm / sm / sam across slots × classes.
// `onLocalApply` receives: Array<{ slotIndex, classIdx, field, value }>

interface ClassesFilterProps {
  isOpen: boolean;
  onClose: () => void;
  uniqueBlocks?: number[];
  numClasses?: number;
  loading?: boolean;
  onLocalApply?: (
    changes: Array<{
      slotIndex: number;
      classIdx: number;
      field: string;
      value: number;
    }>,
  ) => void;
}

const CLASS_FIELDS_LABELS: { field: string; label: string }[] = [
  { field: "rate", label: "Rate / VC" },
  { field: "bm", label: "Buy Margin (BM)" },
  { field: "sm", label: "Sell Margin (SM)" },
  { field: "sam", label: "Sell Aggressive Margin (SAM)" },
];

const ClassesFilter: React.FC<ClassesFilterProps> = ({
  isOpen,
  onClose,
  uniqueBlocks = [],
  numClasses = 0,
  loading = false,
  onLocalApply,
}) => {
  const [selectedBlocks, setSelectedBlocks] = useState<any[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<any[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  // Pending staged changes deduped by slot__class__field
  type Change = {
    slotIndex: number;
    classIdx: number;
    field: string;
    value: number;
  };
  const [pendingChanges, setPendingChanges] = useState<Record<string, Change>>(
    {},
  );

  // Reset staged changes when modal closes or reopens
  useEffect(() => {
    if (!isOpen) {
      setPendingChanges({});
    }
  }, [isOpen]);

  const blockOptions = useMemo(
    () => uniqueBlocks.map((b) => ({ value: String(b), label: String(b) })),
    [uniqueBlocks],
  );

  const classOptions = useMemo(
    () =>
      Array.from({ length: numClasses }, (_, i) => ({
        value: String(i),
        label: `Class ${i + 1}`,
      })),
    [numClasses],
  );

  const resetForm = useCallback(() => {
    setSelectedBlocks([]);
    setSelectedClasses([]);
    setFieldValues({});
  }, []);

  const hasAnyValue = Object.values(fieldValues).some((v) => v !== "");

  const buildChanges = useCallback(
    (
      blocks: number[],
      classes: number[],
    ): Array<{
      slotIndex: number;
      classIdx: number;
      field: string;
      value: number;
    }> => {
      const changes: Array<{
        slotIndex: number;
        classIdx: number;
        field: string;
        value: number;
      }> = [];

      const targetBlocks = blocks.length > 0 ? blocks : uniqueBlocks;
      const targetClasses =
        classes.length > 0
          ? classes
          : Array.from({ length: numClasses }, (_, i) => i);

      for (const slot of targetBlocks) {
        for (const ci of targetClasses) {
          for (const { field } of CLASS_FIELDS_LABELS) {
            const raw = fieldValues[field];
            if (raw === undefined || raw === "") continue;
            const num = Number(raw);
            if (!Number.isFinite(num)) continue;
            changes.push({ slotIndex: slot, classIdx: ci, field, value: num });
          }
        }
      }
      return changes;
    },
    [fieldValues, uniqueBlocks, numClasses],
  );

  const selectedBlockNums = useMemo(
    () => selectedBlocks.map((b: any) => Number(b.value ?? b)),
    [selectedBlocks],
  );
  const selectedClassIdxs = useMemo(
    () => selectedClasses.map((c: any) => Number(c.value ?? c)),
    [selectedClasses],
  );

  // Apply stages changes locally; modal stays open for more edits.
  const handleApply = () => {
    if (!hasAnyValue) return;
    const changes = buildChanges(selectedBlockNums, selectedClassIdxs);
    if (!changes.length) return;
    setPendingChanges((prev) => {
      const next = { ...prev };
      for (const c of changes) {
        next[`${c.slotIndex}__${c.classIdx}__${c.field}`] = c;
      }
      return next;
    });
    // Clear field inputs so user can stage another set; keep filters.
    setFieldValues({});
  };

  // Submit pushes all staged changes to parent and closes modal.
  const handleSubmit = () => {
    const all = Object.values(pendingChanges);
    if (!all.length) return;
    onLocalApply?.(all);
    resetForm();
    setPendingChanges({});
    onClose();
  };

  const handleClose = () => {
    resetForm();
    setPendingChanges({});
    onClose();
  };

  const pendingCount = Object.keys(pendingChanges).length;

  return (
    <div className="z-[100000]">
      <Modal isOpen={isOpen} onClose={handleClose} width="620px">
        {/* Header */}
        <div
          className="p-3"
          style={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #DFEAF2",
          }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-md font-medium" style={{ color: "#525062" }}>
              Batch Customize Classes
            </h2>
            <button
              onClick={handleClose}
              className="hover:bg-gray-100 p-1 rounded-full transition-colors"
              style={{ color: "#6C757D" }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex mod-table-load-in" style={{ minHeight: "360px" }}>
          {/* Filters panel */}
          <div className="flex bg-[#F3F5F8] p-2" style={{ minHeight: "360px" }}>
            <div
              className="p-4 space-y-3"
              style={{
                width: "240px",
                backgroundColor: "#FFFFFFA3",
                borderRadius: "15px",
              }}
            >
              <h2
                className="text-base font-medium mb-4"
                style={{ color: "#6C757D" }}
              >
                Filters
                <hr className="mt-1 border-[#DFEAF2]" />
              </h2>

              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#6F6D7C" }}
                >
                  Time Block
                </label>
                <SearchBox
                  options={blockOptions}
                  isMany
                  placeholder="All blocks"
                  selectedOptions={selectedBlocks}
                  onChange={setSelectedBlocks}
                  menuPortalTarget={document.body}
                />
              </div>

              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#6F6D7C" }}
                >
                  Class
                </label>
                <SearchBox
                  options={classOptions}
                  isMany
                  placeholder="All classes"
                  selectedOptions={selectedClasses}
                  onChange={setSelectedClasses}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </div>

          {/* Fields panel */}
          <div className="space-y-4 bg-[#F3F5F8] p-2 flex-1">
            <div
              className="p-4 space-y-3"
              style={{
                height: "100%",
                backgroundColor: "#FFFFFFA3",
                borderRadius: "15px",
              }}
            >
              <h2
                className="text-base font-medium mb-4"
                style={{ color: "#6C757D" }}
              >
                Customize Fields
                <hr className="mt-1 border-[#DFEAF2]" />
              </h2>

              <div className="grid grid-cols-2 gap-4">
                {CLASS_FIELDS_LABELS.filter(
                  ({ field }) => field !== "sm" && field !== "sam",
                ).map(({ field, label }) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-[#6C757D] mb-2">
                      {label}
                    </label>
                    <input
                      step="any"
                      inputMode="decimal"
                      type="number"
                      value={fieldValues[field] ?? ""}
                      onChange={(e) =>
                        setFieldValues((p) => ({
                          ...p,
                          [field]: e.target.value,
                        }))
                      }
                      placeholder={`Enter ${label.split(" ")[0]}`}
                      className="w-full px-3 py-2 text-sm rounded-lg border"
                    />
                  </div>
                ))}
              </div>

              {/* SM and SAM on separate full-width rows */}
              {CLASS_FIELDS_LABELS.filter(
                ({ field }) => field === "sm" || field === "sam",
              ).map(({ field, label }) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-[#6C757D] mb-2">
                    {label}
                  </label>
                  <input
                    step="any"
                    inputMode="decimal"
                    type="number"
                    value={fieldValues[field] ?? ""}
                    onChange={(e) =>
                      setFieldValues((p) => ({
                        ...p,
                        [field]: e.target.value,
                      }))
                    }
                    placeholder={`Enter ${label.split(" ")[0]}`}
                    className="w-full px-3 py-2 text-sm rounded-lg border"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 flex justify-end items-center gap-4 mod-table-load-in"
          style={{ backgroundColor: "#FFFFFF", borderTop: "1px solid #E1E5E9" }}
        >
          <button
            type="button"
            onClick={() => {
              resetForm();
              setPendingChanges({});
            }}
            className="text-[#9b9aaa] hover:text-[#7d7c8b] text-sm font-medium transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={handleApply}
            disabled={!hasAnyValue || loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-primary bg-white text-primary hover:bg-[#F1935C]/5 transition-all disabled:opacity-40"
          >
            Apply
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || pendingCount === 0}
            className="px-4 py-2 text-sm font-medium rounded-lg border bg-[#F1935C] text-white hover:bg-[#e6844b] transition-all disabled:bg-[#E2E2E5] disabled:text-[#A9A8B1]"
          >
            {loading ? "Saving..." : `Submit Changes`}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ClassesFilter;
